export type DisplayMode = "auto" | "desktop" | "mobile";

export interface DisplayModePreference {
	autoThresholdPx?: number;
	mode: DisplayMode;
}

const storageKey = "display.mode";

export const defaultDisplayModePreference: DisplayModePreference = { mode: "auto" };

export const isDefaultDisplayModePreference = (preference: DisplayModePreference): boolean =>
	preference.mode === "auto" && preference.autoThresholdPx === undefined;

export const displayModePreferenceForMode = (mode: DisplayMode): DisplayModePreference =>
	mode === "auto" ? defaultDisplayModePreference : { mode };

const isDisplayMode = (value: unknown): value is DisplayMode =>
	value === "auto" || value === "desktop" || value === "mobile";

const normalizeThreshold = (value: unknown): number | undefined =>
	typeof value === "number" && Number.isFinite(value) && value > 0 ? Math.round(value) : undefined;

export const readDisplayModePreference = (): DisplayModePreference => {
	try {
		const raw = localStorage.getItem(storageKey);
		if (!raw) return defaultDisplayModePreference;
		const parsed = JSON.parse(raw) as Partial<DisplayModePreference>;
		if (!isDisplayMode(parsed.mode)) return defaultDisplayModePreference;
		return { autoThresholdPx: normalizeThreshold(parsed.autoThresholdPx), mode: parsed.mode };
	} catch {
		return defaultDisplayModePreference;
	}
};

export const writeDisplayModePreference = (preference: DisplayModePreference): void => {
	localStorage.setItem(storageKey, JSON.stringify(preference));
};

export const resolveIsWideScreen = (
	windowWidth: number,
	preference: DisplayModePreference,
	defaultThreshold: number
): boolean => {
	if (preference.mode === "desktop") return true;
	if (preference.mode === "mobile") return false;
	return windowWidth >= (preference.autoThresholdPx ?? defaultThreshold);
};

export const isNarrowForcedDesktop = (
	windowWidth: number,
	preference: DisplayModePreference,
	defaultThreshold: number
): boolean => windowWidth < defaultThreshold && resolveIsWideScreen(windowWidth, preference, defaultThreshold);

export const resolveProfileMenuDisplaySwitch = (
	windowWidth: number,
	preference: DisplayModePreference,
	defaultThreshold: number
): DisplayMode | null => {
	if (!resolveIsWideScreen(windowWidth, preference, defaultThreshold)) return "desktop";
	if (isNarrowForcedDesktop(windowWidth, preference, defaultThreshold)) return "mobile";
	return null;
};

export const thresholdForDesktopAt = (windowWidth: number): number => windowWidth;

export const thresholdForMobileAt = (windowWidth: number): number => windowWidth + 1;
